import axios from 'axios';
import { presignURL } from '@/auth/utils';
import notifications from '@/utils/notifications';
import { brand as brandConstants } from '@/store/constants';
import { translateApiError } from '@/utils/apiError';

const {
  UPDATE_TITLE,
  UPDATE_COPYRIGHT_TEXT,
  UPDATE_THEME,
  UPDATE_LOGO,
  UPDATE_FEEDBACK_URL,
  CLEAR_LOGO_FILE,
  SET_BRAND,
  TOGGLE_PREVIEW,
} = brandConstants.mutations;

const BRAND_ID = '5ae19cdbf907fca0281fda18';

const standardTheme = {
  primary: '#002F87',
  accent: '#0099CB',
  secondary: '#59595C',
  info: '#2196F3',
  warning: '#FFC107',
  error: '#FF5252',
  success: '#4CAF50',
};

const initialBrand = {
  _id: BRAND_ID,
  title: '',
  copyrightText: '',
  feedbackUrl: '',
  logo: {
    url: '',
    logoFile: '',
    name: '',
    redirectUrl: '',
    preview: '',
  },
  theme: {
    ...standardTheme,
  },
};

const initialState = {
  preview: false,
  editing: {
    ...initialBrand,
  },
  canonical: {
    ...initialBrand,
  },
};

// getters
/* eslint-disable no-confusing-arrow */
const getters = {
  logoUrl: (state) =>
    state.preview ? state.editing.logo.preview || state.editing.logo.url : state.canonical.logo.url,
  brand: (state) => (state.preview ? state.editing : state.canonical),
  edited: (state) => JSON.stringify(state.editing) !== JSON.stringify(state.canonical),
};
/* eslint-enable no-confusing-arrow */

// actions
const actions = {
  updateTitle({ commit }, title) {
    commit(UPDATE_TITLE, title);
  },
  updateCopyrightText({ commit }, copyrightText) {
    commit(UPDATE_COPYRIGHT_TEXT, copyrightText);
  },
  updateTheme({ commit }, theme) {
    commit(UPDATE_THEME, theme);
  },
  resetTheme({ commit }) {
    commit(UPDATE_THEME, { ...standardTheme });
  },
  updateLogo({ commit }, logo) {
    commit(UPDATE_LOGO, logo);
  },
  updateFeedbackUrl({ commit }, feedbackUrl) {
    commit(UPDATE_FEEDBACK_URL, feedbackUrl);
  },
  clearLogoFile({ commit }) {
    commit(CLEAR_LOGO_FILE);
  },
  setBrand({ commit }, brand) {
    commit(SET_BRAND, brand);
  },
  togglePreview({ commit }, bool) {
    commit(TOGGLE_PREVIEW, bool);
  },
  resetBrand({ state, dispatch }) {
    dispatch('updateBrand', state.canonical);
  },
  async updateBrand({ dispatch }, brand) {
    await dispatch('setBrand', {
      ...brand,
      logo: {
        ...brand.logo,
        url: await presignURL({ id: brand._id, resource: 'brands' }),
      },
    });
  },
  async fetchBrand({ dispatch }) {
    try {
      await dispatch('updateBrand', (await axios.get(`brands/${BRAND_ID}`)).data);
    } catch (error) {
      notifications.$emit('error', translateApiError(error, 'errors.loadFailed.brand'));
    }
  },
  async saveBrand({ dispatch, state }) {
    const formData = new FormData();
    const { editing } = state;

    ['_id', 'title', 'copyrightText', 'feedbackUrl']
      .filter((key) => editing[key])
      .forEach((key) => {
        formData.append(key, editing[key]);
      });

    ['logoFile', 'name', 'redirectUrl']
      .filter((key) => editing.logo[key])
      .forEach((key) => {
        formData.append(key, editing.logo[key]);
      });

    Object.keys(editing.theme)
      .filter((key) => editing.theme[key])
      .forEach((key) => {
        formData.append(key, editing.theme[key]);
      });

    await dispatch('updateBrand', (await axios.put(`brands/${BRAND_ID}`, formData)).data);
  },
};

// mutations
/* eslint-disable no-param-reassign */
const mutations = {
  [UPDATE_TITLE](state, title) {
    state.editing.title = title;
  },
  [UPDATE_COPYRIGHT_TEXT](state, copyrightText) {
    state.editing.copyrightText = copyrightText;
  },
  [UPDATE_THEME](state, theme) {
    state.editing.theme = { ...state.editing.theme, ...theme };
  },
  [UPDATE_LOGO](state, logo) {
    state.editing.logo = { ...state.editing.logo, ...logo };
  },
  [UPDATE_FEEDBACK_URL](state, feedbackUrl) {
    state.editing.feedbackUrl = feedbackUrl;
  },
  [CLEAR_LOGO_FILE](state) {
    state.editing.logo.logoFile = undefined;
  },
  [SET_BRAND](state, brand) {
    state.editing = { ...brand };
    state.canonical = { ...brand };
  },
  [TOGGLE_PREVIEW](state, bool) {
    if (typeof bool === 'undefined') {
      state.preview = !state.preview;
    } else {
      state.preview = bool;
    }
  },
};
/* eslint-enable no-param-reassign */

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
